import type { QuoteDocument, OptionsDocument } from 'paintscout'
import { getQuoteTypeOptions } from '../../builder/options'
import { parseNumber } from '../../calculator/util/parse-number'

type QuoteTypeSubTotals = {
  [key: string]: {
    price: number
    hours: number
    materials: number
    labor: number
    prep: number
  }
}

export const getQuoteTypeSubTotals = (args: { quote: QuoteDocument; options: OptionsDocument }): QuoteTypeSubTotals => {
  const { quote, options } = args
  let subTotals = {
    int: {
      price: 0,
      hours: 0,
      materials: 0,
      labor: 0,
      prep: 0
    },
    ext: {
      price: 0,
      hours: 0,
      materials: 0,
      labor: 0,
      prep: 0
    },
    all: {
      price: 0,
      hours: 0,
      materials: 0,
      labor: 0,
      prep: 0
    }
  }

  if (!quote) {
    return subTotals
  }

  const lineItems = Object.keys(quote.lineItems ?? {})
  const areas = Object.keys(quote.areas ?? {})
  const groups = Object.keys(quote.groups ?? {})
  const quoteTypeOptions = getQuoteTypeOptions({ options })
  const getValidQuoteType = (value: string | null | undefined): string =>
    value && quoteTypeOptions.find((option) => option.value === value) ? value : 'all'

  if (lineItems.length > 0) {
    lineItems.forEach((lineItemKey) => {
      const lineItem = quote.lineItems[lineItemKey]
      if (lineItem) {
        const quoteType =
          lineItem?.quoteType && quoteTypeOptions.find((option) => option.value === lineItem?.quoteType)
            ? lineItem.quoteType
            : 'all'
        const { afterMaterials: price = 0, materials = 0 } = lineItem?.totals ?? {}
        const { hours = 0 } = lineItem
        const labor = price - materials

        if (lineItem.use_total) {
          subTotals = {
            ...subTotals,
            [quoteType]: subTotals[quoteType]
              ? {
                  price: (subTotals[quoteType]?.price ?? 0) + price,
                  materials: (subTotals[quoteType]?.materials ?? 0) + materials,
                  hours: (subTotals[quoteType]?.hours ?? 0) + hours,
                  prep: subTotals[quoteType]?.prep ?? 0,
                  labor: (subTotals[quoteType]?.labor ?? 0) + labor
                }
              : {
                  price,
                  materials,
                  hours,
                  prep: 0,
                  labor
                }
          }
        }
      }
    })
  }

  if (areas.length > 0) {
    areas.forEach((areaKey) => {
      const area = quote.areas[areaKey]
      if (!area) {
        return
      }

      const { totals, int_ext } = area
      const {
        price,
        override_price_value,
        override_price,
        materials,
        override_materials,
        override_materials_value,
        hours,
        prep
      } = totals

      const materialsPrice = override_materials ? override_materials_value : materials
      const adjustedPrice = override_price ? override_price_value : price
      const totalPrice = adjustedPrice + materialsPrice

      const quoteType =
        int_ext?.value && quoteTypeOptions.find((option) => option.value === int_ext?.value) ? int_ext?.value : 'all'

      if (area && area.use_total) {
        subTotals = {
          ...subTotals,
          [quoteType]: subTotals[quoteType]
            ? {
                price: (subTotals[quoteType]?.price ?? 0) + totalPrice,
                materials: (subTotals[quoteType]?.materials ?? 0) + materialsPrice,
                hours: (subTotals[quoteType]?.hours ?? 0) + hours,
                prep: (subTotals[quoteType]?.prep ?? 0) + prep,
                labor: (subTotals[quoteType]?.labor ?? 0) + adjustedPrice
              }
            : {
                price: totalPrice,
                materials: materialsPrice,
                labor: adjustedPrice,
                hours,
                prep
              }
        }
      }
    })
  }

  if (groups.length > 0) {
    const groupChildCache = {}
    const pricedGroupKeys = getPricedGroupKeys(quote)
    groups.forEach((groupKey) => {
      const group = quote.groups[groupKey]
      // Price each quote type got from this group's children, so the group price can replace it below
      const childPrices: { [quoteType: string]: number } = {}

      group.children.forEach((child) => {
        if (quote?.lineItems?.[child]) {
          const lineItem = quote.lineItems[child]
          if (lineItem.use_total) {
            const quoteType = getValidQuoteType(lineItem.quoteType)
            childPrices[quoteType] = (childPrices[quoteType] ?? 0) + (lineItem.totals?.afterMaterials ?? 0)
          }
        } else if (quote?.areas?.[child]) {
          const area = quote.areas[child]
          if (area.use_total && area.totals) {
            const { price, override_price_value, override_price, materials, override_materials, override_materials_value } =
              area.totals
            const quoteType = getValidQuoteType(area.int_ext?.value)
            childPrices[quoteType] =
              (childPrices[quoteType] ?? 0) +
              parseNumber(override_price ? override_price_value : price) +
              parseNumber(override_materials ? override_materials_value : materials)
          }
        } else if (quote?.substrates?.[child] && !groupChildCache?.[child]) {
          // If substrate, add totals to group
          const substrate = quote?.substrates[child]

          let quoteType
          if (substrate.int_ext && substrate.int_ext.value) {
            quoteType = substrate.int_ext.value
          }

          quoteType = quoteType && quoteTypeOptions.find((option) => option.value === quoteType) ? quoteType : 'all'
          const { price = 0, materials = 0, hours = 0, prep = 0 } = substrate?.grouped_totals ?? {}
          const labor = price - materials
          // const price = substrate?.grouped_totals?.price ?? 0

          subTotals = {
            ...subTotals,
            [quoteType]: subTotals[quoteType]
              ? {
                  price: (subTotals[quoteType]?.price ?? 0) + price,
                  materials: (subTotals[quoteType]?.materials ?? 0) + materials,
                  hours: (subTotals[quoteType]?.hours ?? 0) + hours,
                  prep: (subTotals[quoteType]?.prep ?? 0) + prep,
                  labor: (subTotals[quoteType]?.labor ?? 0) + labor
                }
              : {
                  price,
                  materials,
                  labor,
                  hours,
                  prep
                }
          }
          childPrices[quoteType] = (childPrices[quoteType] ?? 0) + price

          // Cache so we only add grouped_total for this substrate once
          groupChildCache[child] = true
        }
      })

      if (!pricedGroupKeys.has(groupKey) || !group.price) {
        return
      }

      // The quote subtotal uses the group price (custom or default), not the sum of its children
      // (see calculator/groups/sum-group-children). Spread the difference over the children's
      // quote types by their share, so the type rows still add up to the subtotal.
      const groupPrice = parseNumber(group.price.useCustom ? group.price.custom : group.price.default)
      const childTotal = Object.values(childPrices).reduce((sum, price) => sum + price, 0)
      const difference = groupPrice - childTotal
      if (!difference) {
        return
      }

      const shares: { [quoteType: string]: number } = childTotal
        ? Object.fromEntries(Object.entries(childPrices).map(([quoteType, price]) => [quoteType, price / childTotal]))
        : { [getValidQuoteType(group.quoteType)]: 1 }

      Object.entries(shares).forEach(([quoteType, share]) => {
        const adjustment = difference * share
        const current = subTotals[quoteType] ?? { price: 0, materials: 0, hours: 0, prep: 0, labor: 0 }
        subTotals = {
          ...subTotals,
          [quoteType]: {
            ...current,
            price: current.price + adjustment,
            labor: current.labor + adjustment
          }
        }
      })
    })
  }

  return subTotals
}

/**
 * Keys of the groups that the quote subtotal prices: in the order, not deleted, in the bid section
 */
function getPricedGroupKeys(quote: QuoteDocument): Set<string> {
  const keys = new Set<string>()
  quote.order?.area?.forEach((item) => {
    const group = item.type === 'group' && !item._deleted ? quote.groups?.[item.key] : null
    if (group && !group._deleted && group.section === 'bid') {
      keys.add(item.key)
    }
  })
  return keys
}
